// Which publisher-admin tabs offer All stations, per ADR-0036.
//
// The rule is READING ROWS vs WRITING CONFIG, not a hardcoded list of three:
//
//   • A SCOPE-FLEXIBLE tab reads a set of rows where each row belongs to
//     exactly one station, so the set can widen — Overview, Submissions,
//     Advertisers.
//   • A STATION-BOUND tab writes configuration to one specific station, so
//     widening is meaningless: Settings (Team and Payout method), Channel
//     settings, Promotions, Materials, Referrals, My Referrals, and every
//     detail page.
//
// That distinction is what tells you where a fourteenth tab belongs. It also
// depends on the one-station-per-submission rule (CTV-531) for the row-level
// attribution that makes widening honest.
//
// Detail pages are station-bound even under a scope-flexible parent: acting
// on one submission happens at one station, so /admin/all/submissions/:id
// collapses.

/** The URL's slug segment when Station scope is All stations. */
export { ALL_STATIONS_SLUG } from './lastAdminStation';

// Path suffixes (everything after `/admin/<slug>`) that may render in
// aggregated mode. `''` is the index route, i.e. Overview.
// CTV-776: Billing is scope-flexible too (its ledger groups by station).
const SCOPE_FLEXIBLE_SUFFIXES = ['', '/submissions', '/advertisers', '/billing'];

// Every tab ROOT under /admin/<slug>, longest first so a prefix match picks
// the most specific one. Anything deeper is a record id, which belongs to one
// station and cannot survive a station change.
// CTV-787: the two settings tabs are roots in their own right, ahead of bare
// `/settings`, so a station swap on the Payout method tab keeps the tab
// instead of truncating to the Team tab. A tab is not a record: every station
// has both, so there is nothing to 404 on.
const TAB_ROOTS = [
  '/settings/payout-method',
  '/settings/team',
  '/channel-settings',
  '/my-referrals',
  '/advertisers',
  '/submissions',
  '/promotions',
  '/materials',
  '/referrals',
  '/settings',
  '/payments',
  '/billing',
  '/team',
];

/**
 * `pathname` → the part after `/admin/<slug>`. Returns '' for the index.
 * Mirrors the segment-swap regex the scope selector uses, so the two agree
 * about what a "tab" is.
 */
export function adminTabSuffix(pathname: string): string {
  return pathname.replace(/^\/admin\/[^/]+/, '').replace(/\/$/, '');
}

/** Can this path render with Station scope = All stations? */
export function isScopeFlexiblePath(pathname: string): boolean {
  return SCOPE_FLEXIBLE_SUFFIXES.includes(adminTabSuffix(pathname));
}

/**
 * Where changing Station scope should land, preserving the TAB.
 *
 * `/admin/kezi/submissions` + 'wxow' → `/admin/wxow/submissions`.
 *
 * Detail pages truncate to their tab root: `/admin/kezi/submissions/<id>`
 * becomes `/admin/wxow/submissions`, not `/admin/wxow/submissions/<id>`.
 * A record id belongs to exactly one station, so carrying it across would
 * navigate the viewer straight into a 404.
 */
export function withAdminSlug(pathname: string, slug: string): string {
  const suffix = adminTabSuffix(pathname);
  const root = TAB_ROOTS.find((r) => suffix === r || suffix.startsWith(`${r}/`)) ?? '';
  return `/admin/${slug}${root}`;
}
